import React, { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { SendOutlined } from '@ant-design/icons';

import {
  fetchAgenticRagHealth,
  streamAgenticQuery,
} from '../../../services/agenticRagService';
import { useUser } from '../../../context/UserContext';
import { renderMarkdown } from './AgenticRagMarkdown';
import './AgenticRagChatPage.css';

function confBadgeClass(c) {
  if (c === 'High') return 'high';
  if (c === 'Low') return 'low';
  return 'medium';
}

function createMessage(role, partial = {}) {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    role,
    text: '',
    steps: [],
    meta: null,
    error: null,
    streaming: false,
    ...partial,
  };
}

const CHAT_STORAGE_PREFIX = 'fi-rag-chat';

function getChatStorageKey(userData) {
  const userKey = userData?.id || userData?.email || 'anonymous';
  return `${CHAT_STORAGE_PREFIX}:${userKey}`;
}

function sanitizeMessagesForStorage(list) {
  return list
    .filter((m) => m?.role === 'user' || m?.role === 'agent')
    .map((m) => ({
      id: m.id,
      role: m.role,
      text: m.text || '',
      steps: Array.isArray(m.steps) ? m.steps : [],
      meta: m.meta || null,
      error: m.error || null,
      streaming: false,
    }));
}

function StepsPanel({ steps }) {
  const [open, setOpen] = useState(true);
  if (!steps.length) return null;

  return (
    <div className="arag-steps-panel">
      <div
        className={`arag-steps-toggle ${open ? 'open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => e.key === 'Enter' && setOpen((v) => !v)}
        role="button"
        tabIndex={0}
      >
        <span className="arrow">▶</span>
        Agent steps
        <span style={{ marginLeft: 'auto', fontSize: 10 }}>
          {steps.length}
          {' '}
          step
          {steps.length > 1 ? 's' : ''}
        </span>
      </div>
      <div className={`arag-steps-body ${open ? 'visible' : ''}`}>
        {steps.map((s, i) => (
          <div key={i}>{s}</div>
        ))}
      </div>
    </div>
  );
}

function AgentMessage({ msg }) {
  const { meta } = msg;

  return (
    <>
      <StepsPanel steps={msg.steps} />

      {meta && (
        <div className="arag-meta-row">
          {meta.domain && (
            <span className="arag-badge domain">📂 {meta.domain}</span>
          )}
          {meta.confidence && (
            <span className={`arag-badge ${confBadgeClass(meta.confidence)}`}>
              ●
              {meta.confidence}
              {' '}
              confidence
            </span>
          )}
          {meta.sql_row_count != null && (
            <span className="arag-badge source">
              🗄
              {meta.sql_row_count}
              {' '}
              DB rows
            </span>
          )}
          {meta.entities?.map((e) => (
            e ? <span key={e} className="arag-badge entity">🏢 {e}</span> : null
          ))}
        </div>
      )}

      {msg.error ? (
        <span className="arag-error-text">⚠️ {msg.error}</span>
      ) : (
        <div
          className="arag-md-content"
          dangerouslySetInnerHTML={{
            __html: renderMarkdown(msg.text || (msg.streaming ? 'Thinking…' : '')),
          }}
        />
      )}

      {msg.streaming && !msg.error && <span className="arag-cursor" aria-hidden />}

      {meta?.web_sources?.length > 0 && (
        <div className="arag-sources-row">
          <div className="src-label">🌐 Web sources</div>
          {meta.web_sources.map((url, i) => (
            <a key={url} href={url} target="_blank" rel="noopener noreferrer">
              [{i + 1}] {url}
            </a>
          ))}
        </div>
      )}

      {meta?.pdf_sources?.length > 0 && (
        <div className="arag-sources-row">
          <div className="src-label">📄 Bond documents</div>
          {meta.pdf_sources.map((s) => (
            <div key={s} style={{ color: 'var(--arag-muted)', fontSize: 12 }}>📄 {s}</div>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Chat messages + input only (no sidebar). Used in popup widget and full-page views.
 */
export default function AgenticRagChatPanel({ compact = false, clearSignal = 0 }) {
  const { userData } = useUser();
  const [serverStatus, setServerStatus] = useState('checking');
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const storageKey = getChatStorageKey(userData);
  const hydratedKeyRef = useRef('');
  const clearSignalRef = useRef(clearSignal);

  const abortRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const checkHealth = useCallback(async () => {
    setServerStatus('checking');
    try {
      await fetchAgenticRagHealth();
      setServerStatus('online');
    } catch {
      setServerStatus('offline');
    }
  }, []);

  useEffect(() => {
    checkHealth();
  }, [checkHealth]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (hydratedKeyRef.current === storageKey) return;
    hydratedKeyRef.current = storageKey;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        setMessages([]);
        return;
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setMessages(sanitizeMessagesForStorage(parsed));
      } else {
        setMessages([]);
      }
    } catch {
      setMessages([]);
      // ignore malformed local storage payload
    }
  }, [storageKey]);

  useEffect(() => {
    try {
      if (messages.length === 0) {
        localStorage.removeItem(storageKey);
        return;
      }
      localStorage.setItem(storageKey, JSON.stringify(sanitizeMessagesForStorage(messages)));
    } catch {
      // ignore storage quota/security errors
    }
  }, [messages, storageKey]);

  useEffect(() => {
    if (clearSignalRef.current === clearSignal) return;
    clearSignalRef.current = clearSignal;
    setMessages([]);
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // ignore storage quota/security errors
    }
  }, [clearSignal, storageKey]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, compact ? 100 : 140)}px`;
  }, [query, compact]);

  const updateAgentMessage = useCallback((id, updater) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? updater(m) : m)));
  }, []);

  const handleSubmit = async () => {
    const text = query.trim();
    if (!text || loading) return;
    if (serverStatus !== 'online') {
      message.warning('FI Intelligence service is offline.');
      return;
    }

    setQuery('');
    setLoading(true);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    const userMsg = createMessage('user', { text });
    const agentMsg = createMessage('agent', { streaming: true });
    setMessages((prev) => [...prev, userMsg, agentMsg]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const stream = streamAgenticQuery({ query: text }, { signal: controller.signal });

      for await (const ev of stream) {
        if (ev.type === 'step') {
          updateAgentMessage(agentMsg.id, (m) => ({
            ...m,
            steps: [...m.steps, ev.content],
          }));
        } else if (ev.type === 'answer') {
          updateAgentMessage(agentMsg.id, (m) => ({
            ...m,
            text: m.text + ev.content,
          }));
        } else if (ev.type === 'meta') {
          updateAgentMessage(agentMsg.id, (m) => ({ ...m, meta: ev.content }));
        } else if (ev.type === 'error') {
          updateAgentMessage(agentMsg.id, (m) => ({ ...m, error: ev.content }));
        }
      }

      updateAgentMessage(agentMsg.id, (m) => ({ ...m, streaming: false }));
    } catch (err) {
      if (err.name !== 'AbortError') {
        updateAgentMessage(agentMsg.id, (m) => ({
          ...m,
          streaming: false,
          error: err.message || 'Failed to fetch',
        }));
      }
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  const canSend = Boolean(query.trim()) && !loading && serverStatus === 'online';

  return (
    <div className={`arag-panel ${compact ? 'arag-panel--compact' : ''}`}>
      {serverStatus === 'offline' && (
        <div className="arag-offline-banner arag-offline-banner--compact">
          Service offline — start API Gateway (8080) and Agentic RAG (8091)
        </div>
      )}

      <div className="arag-chat-area arag-chat-area--solo">
        <div className="arag-messages">
          {messages.length === 0 ? (
            <div className="arag-welcome arag-welcome--compact">
              <div className="big-logo">🤖</div>
              <h2>Financial Intelligence</h2>
              <p>Ask about issuers, bonds, financials, or industry trends.</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div key={msg.id} className="arag-message-wrap">
                <div className={`arag-msg ${msg.role}`}>
                  <div className="arag-avatar">{msg.role === 'user' ? '👤' : '🤖'}</div>
                  <div className="arag-bubble">
                    {msg.role === 'user' ? msg.text : <AgentMessage msg={msg} />}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="arag-input-area">
          <div className="arag-input-inner">
            <textarea
              ref={textareaRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ask a financial question…"
              rows={1}
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (canSend) handleSubmit();
                }
                if (e.key === 'Escape' && abortRef.current) {
                  abortRef.current.abort();
                }
              }}
            />
            <button
              type="button"
              className="arag-send-btn"
              disabled={!canSend}
              onClick={handleSubmit}
              title="Send"
            >
              <SendOutlined style={{ fontSize: 18 }} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function useAgenticRagHealth() {
  const [status, setStatus] = useState('checking');
  const [llmProvider, setLlmProvider] = useState('');

  const check = useCallback(async () => {
    setStatus('checking');
    try {
      const data = await fetchAgenticRagHealth();
      setLlmProvider(data.llm_provider || '');
      setStatus('online');
    } catch {
      setStatus('offline');
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  return { status, llmProvider, refresh: check };
}
